import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocket } from "context/WebSocketContext";
import {
  getTransferPage,
  getTransferSummary,
  normalizeTransfer,
} from "./transferApi";

const emptySummary = {
  totalIn: 0,
  totalOut: 0,
  netGallons: 0,
  totalTimeSeconds: 0,
  transferCount: 0,
  warningCount: 0,
  invalidCount: 0,
  openCount: 0,
};

function mergeVersioned(current, incoming) {
  const byId = new Map(current.map((item) => [item.id, item]));
  const existing = byId.get(incoming.id);
  if (!existing || incoming.version >= existing.version) byId.set(incoming.id, incoming);
  return [...byId.values()].sort((a, b) => Date.parse(b.startedAt || 0) - Date.parse(a.startedAt || 0));
}

export default function useTransferDay({ deviceId, from, to }) {
  const { tabletTransferUpdated } = useWebSocket();
  const [transfers, setTransfers] = useState([]);
  const [summary, setSummary] = useState(emptySummary);
  const [nextCursor, setNextCursor] = useState(null);
  const [status, setStatus] = useState("loading");
  const [errorCode, setErrorCode] = useState(null);
  const requestId = useRef(0);

  const refresh = useCallback(async () => {
    if (!deviceId || !from || !to) return;
    const request = ++requestId.current;
    const controller = new AbortController();
    setStatus("loading");
    setErrorCode(null);
    try {
        const [pageResult, summaryResult] = await Promise.allSettled([
          getTransferPage({ deviceId, from, to, limit: 50, signal: controller.signal }),
          getTransferSummary({ deviceId, from, to, signal: controller.signal }),
        ]);
      if (request !== requestId.current) return;
        if (pageResult.status === "fulfilled") {
          setTransfers(pageResult.value.transfers);
          setNextCursor(pageResult.value.nextCursor);
        }
        if (summaryResult.status === "fulfilled") {
          setSummary(summaryResult.value ?? emptySummary);
        }
        if (pageResult.status === "rejected" && summaryResult.status === "rejected") {
          throw pageResult.reason;
        }
        setStatus(pageResult.status === "fulfilled" ? "ready" : "unavailable");
        if (pageResult.status === "rejected") {
          setErrorCode(pageResult.reason?.error?.code ?? pageResult.reason?.code ?? "TRANSFER_HISTORY_UNAVAILABLE");
        } else if (summaryResult.status === "rejected") {
          setErrorCode(summaryResult.reason?.error?.code ?? summaryResult.reason?.code ?? "TRANSFER_SUMMARY_UNAVAILABLE");
        }
    } catch (error) {
      if (error?.name === "CanceledError" || error?.name === "AbortError") return;
      if (request !== requestId.current) return;
      setStatus("unavailable");
      setErrorCode(error?.error?.code ?? error?.code ?? "TRANSFER_HISTORY_UNAVAILABLE");
    }
  }, [deviceId, from, to]);

  const loadMore = useCallback(async () => {
    if (!deviceId || !from || !to || !nextCursor) return;
    try {
      const page = await getTransferPage({ deviceId, from, to, cursor: nextCursor, limit: 50 });
      setTransfers((current) => page.transfers.reduce(mergeVersioned, current));
      setNextCursor(page.nextCursor);
    } catch (error) {
      setStatus("unavailable");
      setErrorCode(error?.error?.code ?? error?.code ?? "TRANSFER_HISTORY_UNAVAILABLE");
    }
  }, [deviceId, from, to, nextCursor]);

  useEffect(() => {
    refresh();
    return () => { requestId.current += 1; };
  }, [refresh]);

  useEffect(() => {
    const payload = tabletTransferUpdated;
    if (!payload || String(payload.device_id) !== String(deviceId) || !payload.transfer) return;
    const transfer = normalizeTransfer(payload.transfer);
    if (!transfer.id || transfer.businessDate !== from || from !== to) {
      refresh();
      return;
    }
    setTransfers((current) => mergeVersioned(current, transfer));
    if (transfer.status === "stopped") {
      console.log("Transfer completed (stopped):", transfer.id);
      // Add specific completion logic here (e.g., trigger a notification or sound)
    }
    // Lists update immediately; summaries are server-authoritative and may include validation rules.
    refresh();
  }, [tabletTransferUpdated, deviceId, from, to, refresh]);

  return {
    transfers,
    summary,
    status,
    errorCode,
    nextCursor,
    refresh,
    loadMore,
  };
}
